import React, { FC } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, Box } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  CustomFormulaForm,
  CustomFormulaValues,
  NumberType,
} from 'src/components/Forms/CustomFormulaForm';
import { customFormulaFormSchema } from 'src/components/Forms/validationSchemas';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { unformatCustomFormulaItems } from 'src/utils';
import { CREATE_CUSTOM_FORMULA } from 'src/utils/gql/mutations';
import { GET_ALL_CUSTOM_FORMULAS } from 'src/utils/gql/queries';
import {
  MetricFormatType,
  Mutation,
  MutationCreateCustomFormulaArgs,
} from 'src/utils/gql/types';

// Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
const CustomFormulaContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  padding: '24px',
  marginLeft: '240px',
  width: 'calc(100% - 240px)',
  maxWidth: 'calc(100% - 240px)',
  boxSizing: 'border-box',
  overflow: 'visible', // Permette overflow per evitare tagli

  [theme.breakpoints.down('lg')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
    padding: '20px',
  },

  [theme.breakpoints.down('md')]: {
    marginLeft: '200px',
    width: 'calc(100% - 200px)',
    maxWidth: 'calc(100% - 200px)',
    padding: '16px',
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: '0',
    width: '100%',
    padding: '16px',
    marginTop: '60px', // Spazio per header mobile
  },

  [theme.breakpoints.down('xs')]: {
    padding: '12px',
  },
}));

// Container interno completamente fluido e responsive
const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '0',
  boxSizing: 'border-box',
  overflow: 'visible', // Permette overflow per evitare tagli

  '.MuiTypography-h3': {
    marginBottom: '30px',

    [theme.breakpoints.down('sm')]: {
      marginBottom: '20px',
      textAlign: 'center',
    },
  },
}));

export const CreateCustomFormula: FC = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const [createFormula, { loading }] = useMutation<
    Pick<Mutation, 'createCustomFormula'>,
    MutationCreateCustomFormulaArgs
  >(CREATE_CUSTOM_FORMULA, {
    refetchQueries: [{ query: GET_ALL_CUSTOM_FORMULAS }],
    onCompleted: () => {
      snackbar('A custom formula has been successfully created.', 'success');
      navigate(`/${ROUTE_SOURCES.SETTINGS}`);
    },
    onError,
  });

  const handleSubmit: SubmitHandler<CustomFormulaValues> = ({
    name,
    format,
    formulaItems,
  }) => {
    createFormula({
      variables: {
        customFormulaData: {
          name,
          customFormulaItems: unformatCustomFormulaItems(formulaItems),
          format: {
            type: format.type,
            description: format.description || '',
            suffix:
              format.type === MetricFormatType.Number
                ? format.suffix
                : undefined,
            prefix:
              format.type === MetricFormatType.Number
                ? format.prefix
                : undefined,
            isDecimal:
              format.type === MetricFormatType.Number
                ? format.numberType === NumberType.Decimal
                : true,
          },
        },
      },
    });
  };

  const handleClickCancel = () => navigate(`/${ROUTE_SOURCES.SETTINGS}`);

  const formMethods = useForm<CustomFormulaValues>({
    mode: 'onChange',
    resolver: yupResolver(customFormulaFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      formulaItems: [],
      format: { type: MetricFormatType.Number, numberType: NumberType.Decimal },
    },
  });

  return (
    <CustomFormulaContainer>
      <ContentContainer>
        <Typography variant="h3">Create Custom formula</Typography>
        <FormProvider {...formMethods}>
          <CustomFormulaForm
            onSubmit={handleSubmit}
            onCancel={handleClickCancel}
            buttonLoading={loading}
          />
        </FormProvider>
      </ContentContainer>
    </CustomFormulaContainer>
  );
};
